export interface ExpirableNotification {
  expiresAt?: number | null;
}

export interface ReadableNotification {
  unread?: boolean;
}

export function isNotificationExpired(
  notification: ExpirableNotification,
  now = Date.now(),
): boolean {
  return typeof notification.expiresAt === 'number'
    && Number.isFinite(notification.expiresAt)
    && notification.expiresAt <= now;
}

export function keepActiveNotifications<T extends ExpirableNotification>(
  notifications: T[],
  now = Date.now(),
): T[] {
  return notifications.filter((notification) => !isNotificationExpired(notification, now));
}

/**
 * Drop read notifications when a new one arrives so the box stays focused
 * on unread. Unread items are preserved; missing `unread` is treated as unread.
 */
export function dropReadForIncoming<T extends ReadableNotification & { id?: string }>(
  existing: T[],
  incoming: T | T[],
): T[] {
  const next = Array.isArray(incoming) ? incoming : [incoming];
  const incomingIds = new Set(
    next.map((n) => n.id).filter((id): id is string => Boolean(id)),
  );
  const unreadOnly = existing.filter((n) => (
    n.unread !== false && (!n.id || !incomingIds.has(n.id))
  ));
  return [...next, ...unreadOnly];
}

/** First caller for this id wins (FCM + Firestore listener share a tag). */
export function claimNotificationId(seen: Set<string>, id: string | undefined): boolean {
  if (!id) return true;
  if (seen.has(id)) return false;
  seen.add(id);
  return true;
}
